import { useMemo } from "react";

import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import RecordSource from "@mapmycustomers/shared/enum/RecordSource";
import { Group, Territory } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";

import { displayOrderComparator } from "@app/util/comparator";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import loggingService from "@app/util/logging";
import { getRecordSourceTypeDisplayName } from "@app/util/ui";

import Option from "../types/Option";

const useOptions = (
  field: IField,
  groups: Group[],
  funnels: Funnel[],
  territories: Territory[],
  stages: Stage[]
): Option[] => {
  const intl = useIntl();

  return useMemo(() => {
    if (field.hasFeature(FieldFeature.GROUP_FIELD)) {
      return [...groups].sort(nameComparator).map(({ id, name }) => ({
        label: name,
        value: String(id),
      }));
    } else if (field.hasFeature(FieldFeature.TERRITORY_FIELD)) {
      return [...territories].sort(nameComparator).map(({ id, name }) => ({
        label: name,
        value: String(id),
      }));
    } else if (field.hasFeature(FieldFeature.FUNNEL_FIELD)) {
      return [...funnels]
        .sort(nameComparator)
        .map(({ id, name }) => ({ label: name, value: String(id) }));
    } else if (field.hasFeature(FieldFeature.STAGE_FIELD)) {
      return [...stages].map(({ id, funnel, name }) => ({
        groupId: funnel.id,
        label: name,
        value: String(id),
      }));
    } else if (field.hasFeature(FieldFeature.SOURCE_FIELD)) {
      return [...Object.values(RecordSource)].map((value) => ({
        label: getRecordSourceTypeDisplayName(intl, value as RecordSource),
        value,
      }));
    }

    if (isCustomField(field) && field.isOptionField) {
      return field.customFieldData.options.sort(displayOrderComparator).map((option) => ({
        label: option.displayName,
        value: String(option.value),
      }));
    }

    loggingService.warning(`Found no options for field ${field.name}`);
    return [];
  }, [field, funnels, groups, intl, stages, territories]);
};

export default useOptions;
